import React from 'react';
import styled from 'libs/styled';
import { richTextTagStyles } from 'libs/sanity/components/PortableText/richTextTagStyles';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import { shallowEqual } from 'react-redux';
import ContentWrapper from 'components/ContentWrapper';
import PortableText from 'libs/sanity/components/PortableText';
import { Logotype } from 'assets/media/icons/Logotype';
import { above } from '@fogia/core-web/utils/mediaqueries';
import { LinkSections } from './LinkSections';
import { ContactSections } from './ContactSections';
import { BottomBar } from './BottomBar';
import type { FooterData } from './types';

const FooterElement = styled('footer')`
    position: relative;
    z-index: var(--zindex-footer);
    background-color: var(--color-base-brickwork-orange);
    color: var(--color-base-charcoal);
`;

const TopSection = styled('div')`
    display: flex;
    gap: 10px;
    flex-direction: column;

    ${above['tablet.lg']} {
        flex-direction: row;
    }
`;

const Spacer = styled('div')`
    flex: 1;
`;

const DescriptionWrapper = styled('div')`
    flex: 1;
    ${richTextTagStyles}
`;

const BottomSection = styled('div')`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const RightColumn = styled('div')`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 30px;
`;

const LinksContactsRow = styled('div')`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const MiddleSection = styled('div')`
    display: flex;
    flex-direction: column;
`;

const LogoWrapper = styled('div')`
    width: 100%;

    ${above['desktop.sm']} {
        flex: 1;
        display: flex;
        background-image: url('data:image/svg+xml,<svg fill="%23171717" height="40" viewBox="0 0 72 40" width="72" xmlns="http://www.w3.org/2000/svg"><path d="m0 39.399h4.62723v-16.3342h6.93357v-4.213h-6.93357v-13.96204h7.20557v-4.288727h-11.8328z"/><path d="m21.3432 0c-4.092 0-7.5024 3.53635-7.5024 8.57746v22.84504c0 5.1168 3.4104 8.5775 7.5024 8.5775s7.5024-3.4607 7.5024-8.5775v-22.84504c0-5.04111-3.4104-8.57746-7.5024-8.57746zm2.9556 32.3248c0 1.7304-1.1363 3.0852-2.9556 3.0852s-2.9556-1.3548-2.9556-3.0852v-24.65107c0-1.65467 1.0602-3.08521 2.9556-3.08521 1.8953 0 2.9556 1.42909 2.9556 3.08521z"/><path d="m53.5642.601033h-4.658v38.797967h4.658z"/><path d="m39.1019 0c-4.0935 0-7.5039 3.53635-7.5039 8.57746v22.84504c0 6.0948 3.7132 8.5775 6.5942 8.5775 1.7945 0 3.3388-.6199 4.3582-1.7653v.0088c.0058.3871.0482.7742.1243 1.154h3.4763v-20.5312h-7.0491v4.2131h2.9556v9.2469c0 2.1072-.9842 3.4606-2.9556 3.4606s-2.9557-1.3548-2.9557-3.4606v-24.65257c0-1.65467 1.0618-3.08521 2.9557-3.08521s2.9556 1.42909 2.9556 3.08521v7.36667h4.0935v-6.46294c0-5.04111-2.9557-8.57746-7.0491-8.57746z"/><path d="m67.3684.601033h-7.3679l-4.5863 38.797967h4.6228l1.0018-10.27h5.3468l.9916 10.27h4.6228zm-5.9171 24.313467 2.2024-19.23452h.1887l2.1396 19.23452z"/></svg>');
        background-size: contain;
        background-position: top left;
    }
`;

const DESCRIPTION_TYPOGRAPHY = [
    'Secondary/Mobile/13_120_400',
    null,
    null,
    null,
    null,
    'Secondary/20_120_400_-1_Indent',
];

const Footer = () => {
    const footer: FooterData = useAppSelector(({ application }) => application?.config?.footer, shallowEqual);
    const organizationSchema = useAppSelector((state) => state.application?.config?.siteOptions?.organizationSchema);
    const isDesktop = useBreakpoint({ from: 'desktop.sm' });

    if (!footer) return null;

    const { description, linkSections, contactSections } = footer;

    return (
        <FooterElement data-theme="orange">
            <ContentWrapper
                display="flex"
                flexDirection="column"
                maxWidth="var(--site-corridor-max-width)"
                p={['50px 10px 10px 10px', null, null, null, null, '100px 10px 10px 10px']}
                gap={['40px', null, null, null, null, '100px']}
            >
                {description &&
                    (isDesktop ? (
                        <TopSection>
                            <Spacer flex="1" />
                            <DescriptionWrapper>
                                <PortableText value={description} typography={DESCRIPTION_TYPOGRAPHY} />
                            </DescriptionWrapper>
                        </TopSection>
                    ) : (
                        <PortableText value={description} typography={DESCRIPTION_TYPOGRAPHY} />
                    ))}

                {isDesktop ? (
                    <BottomSection>
                        <LogoWrapper />
                        <RightColumn>
                            <LinksContactsRow>
                                <LinkSections sections={linkSections} isDesktop={isDesktop} />
                                <ContactSections sections={contactSections} isDesktop={isDesktop} />
                            </LinksContactsRow>
                            <BottomBar
                                socialMediaLinks={organizationSchema?.socialMediaLinks}
                                phone={organizationSchema?.organization?.telephone}
                                email={organizationSchema?.organization?.email}
                            />
                        </RightColumn>
                    </BottomSection>
                ) : (
                    <>
                        <MiddleSection>
                            <LinkSections sections={linkSections} isDesktop={isDesktop} />
                            <ContactSections sections={contactSections} isDesktop={isDesktop} />
                        </MiddleSection>
                        <BottomBar
                            socialMediaLinks={organizationSchema?.socialMediaLinks}
                            phone={organizationSchema?.organization?.telephone}
                            email={organizationSchema?.organization?.email}
                        />
                        <LogoWrapper>
                            <Logotype color="var(--color-base-charcoal)" width="100%" />
                        </LogoWrapper>
                    </>
                )}
            </ContentWrapper>
        </FooterElement>
    );
};

export default Footer;
